import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { CaseStatusBadge, CaseProgressStepper } from '../components/CaseStatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Briefcase, FileText, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/cases`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setCases(casesRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <DashboardLayout>
      <div data-testid="client-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading">{t('dashboard')}</h1>
          <p className="text-[#475569] mt-1">{t('welcome_back')}, {user?.name}</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-[#7E22CE]" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A] font-heading">{stats.my_cases}</div>
                <div className="text-sm text-[#475569] mt-1">{t('my_cases')}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-[#059669]" />
                </div>
                <div className="text-2xl font-bold text-[#0F172A] font-heading">{stats.active_cases}</div>
                <div className="text-sm text-[#475569] mt-1">{t('active_cases')}</div>
              </CardContent>
            </Card>
          </div>
        )}

        <h2 className="text-xl font-bold text-[#0F172A] font-heading mb-4">{t('my_cases')}</h2>
        {cases.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-12 text-center">
              <Briefcase className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#94A3B8]">{t('no_cases')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {cases.map(c => (
              <Card
                key={c.case_id}
                className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/client/cases/${c.case_id}`)}
                data-testid={`client-case-card-${c.case_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-[#0F172A] font-heading">{c.title}</h3>
                      <p className="text-sm text-[#475569] capitalize mt-1">{c.case_type}</p>
                    </div>
                    <CaseStatusBadge status={c.status} />
                  </div>
                  <CaseProgressStepper currentStatus={c.status} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
